import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api/api";
import {
  ACTIVE_ADDRESS_COUNTRY,
  normalizeCountryCode,
  normalizeUsStateCode,
  type SupportedAddressCountry,
} from "@/lib/config/address";

interface GooglePlaceResult {
  addressComponents?: Array<{ longText?: string; shortText?: string; types?: string[] }>;
  formattedAddress?: string;
  location?: {
    lat?: number | (() => number);
    lng?: number | (() => number);
  };
}

interface ParsedAddressFields {
  addressLine1: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country: SupportedAddressCountry;
}

let googleMapsApiKeyLoader: Promise<string | null> | null = null;
let cachedGoogleMapsApiKey: string | null = null;

async function getGoogleMapsApiKey(forceRefresh = false): Promise<string | null> {
  if (!forceRefresh && cachedGoogleMapsApiKey) {
    return cachedGoogleMapsApiKey;
  }

  if (!googleMapsApiKeyLoader) {
    googleMapsApiKeyLoader = api
      .get<{ apiKey: string | null }>("/client-config/google-maps", { skipToast: true })
      .then((response) => {
        const apiKey = response.apiKey?.trim() || null;
        if (apiKey) {
          cachedGoogleMapsApiKey = apiKey;
        }
        googleMapsApiKeyLoader = null;
        return apiKey;
      })
      .catch(() => {
        // Allow retry after transient auth/network errors.
        googleMapsApiKeyLoader = null;
        return null;
      });
  }

  return googleMapsApiKeyLoader;
}

const GOOGLE_MAPS_LIBRARIES = ["places"] as const;
const PAC_CONTAINER_Z_INDEX = "2147483647";

function normalizePacContainers(): void {
  if (typeof document === "undefined") {
    return;
  }

  const containers = document.querySelectorAll<HTMLElement>(".pac-container");
  containers.forEach((container) => {
    container.style.zIndex = PAC_CONTAINER_Z_INDEX;
    container.style.pointerEvents = "auto";
  });
}

function getAddressValue(components: GooglePlaceResult["addressComponents"], type: string): string | undefined {
  const component = components?.find((item) => item.types?.includes(type));
  return component?.longText;
}

function getAddressShortValue(components: GooglePlaceResult["addressComponents"], type: string): string | undefined {
  const component = components?.find((item) => item.types?.includes(type));
  return component?.shortText;
}

function buildAddressLine1(place: GooglePlaceResult): string {
  const streetNumber = getAddressValue(place.addressComponents, "street_number");
  const route = getAddressValue(place.addressComponents, "route");

  if (streetNumber && route) {
    return `${streetNumber} ${route}`;
  }

  if (route) {
    return route;
  }

  return place.formattedAddress ?? "";
}

function getLocationValue(value?: number | (() => number)): number | undefined {
  if (typeof value === "function") {
    return value();
  }

  return typeof value === "number" ? value : undefined;
}

function parseAddressFields(place: GooglePlaceResult): ParsedAddressFields {
  const addressLine1 = buildAddressLine1(place);
  const city =
    getAddressValue(place.addressComponents, "locality") ||
    getAddressValue(place.addressComponents, "postal_town") ||
    getAddressValue(place.addressComponents, "administrative_area_level_2");
  const state = normalizeUsStateCode(
    getAddressShortValue(place.addressComponents, "administrative_area_level_1") ||
      getAddressValue(place.addressComponents, "administrative_area_level_1"),
  );
  const postalCode = getAddressValue(place.addressComponents, "postal_code") || "";
  const country = normalizeCountryCode(
    getAddressShortValue(place.addressComponents, "country"),
  );

  return {
    addressLine1,
    city,
    state,
    postalCode,
    country,
  };
}

function hasCoreAddressFields(address: ParsedAddressFields): boolean {
  return Boolean(address.city && address.state && address.postalCode);
}

export interface ParsedGoogleAddress {
  addressLine1: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country: SupportedAddressCountry;
  latitude?: number;
  longitude?: number;
}

interface GoogleAddressAutocompleteInputProps {
  id?: string;
  value: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onAddressSelected: (address: ParsedGoogleAddress) => void;
}

interface GoogleAddressAutocompleteLoadedProps extends GoogleAddressAutocompleteInputProps {
  apiKey: string;
}

function toGooglePlaceResult(place: google.maps.places.PlaceResult): GooglePlaceResult {
  return {
    addressComponents: place.address_components?.map((component) => ({
      longText: component.long_name,
      shortText: component.short_name,
      types: component.types,
    })),
    formattedAddress: place.formatted_address,
    location: {
      lat: place.geometry?.location?.lat,
      lng: place.geometry?.location?.lng,
    },
  };
}

function GoogleAddressAutocompleteLoaded({
  id,
  value,
  placeholder,
  className,
  disabled,
  onChange,
  onAddressSelected,
  apiKey,
}: GoogleAddressAutocompleteLoadedProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onAddressSelectedRef = useRef(onAddressSelected);
  const onChangeRef = useRef(onChange);

  const libraries = useMemo(() => [...GOOGLE_MAPS_LIBRARIES], []);
  const { isLoaded } = useJsApiLoader({
    id: "yeschef-google-maps",
    googleMapsApiKey: apiKey,
    libraries,
    version: "weekly",
  });

  useEffect(() => {
    onAddressSelectedRef.current = onAddressSelected;
  }, [onAddressSelected]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let disposed = false;
    if (!isLoaded || typeof window === "undefined" || !inputRef.current) {
      return;
    }

    const places = window.google?.maps?.places;
    if (!places?.Autocomplete) {
      return;
    }

    const autocomplete = new places.Autocomplete(inputRef.current, {
      types: ["address"],
      componentRestrictions: { country: ACTIVE_ADDRESS_COUNTRY.toLowerCase() },
      fields: ["address_components", "formatted_address", "geometry"],
    });

    normalizePacContainers();

    const pacObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => {
            normalizePacContainers();
          })
        : null;

    if (pacObserver && document.body) {
      pacObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener("place_changed", () => {
      if (disposed) {
        return;
      }

      const place = autocomplete.getPlace();
      if (!place) {
        return;
      }

      const parsedPlace = toGooglePlaceResult(place);
      const latitude = getLocationValue(parsedPlace.location?.lat);
      const longitude = getLocationValue(parsedPlace.location?.lng);
      normalizePacContainers();

      const applySelection = (address: ParsedAddressFields) => {
        onChangeRef.current(address.addressLine1);
        onAddressSelectedRef.current({
          addressLine1: address.addressLine1,
          city: address.city,
          state: address.state,
          postalCode: address.postalCode,
          country: address.country,
          latitude,
          longitude,
        });
      };

      const selectedAddress = parseAddressFields(parsedPlace);
      if (hasCoreAddressFields(selectedAddress) || latitude === undefined || longitude === undefined) {
        applySelection(selectedAddress);
        return;
      }

      const geocoder = window.google?.maps?.Geocoder ? new window.google.maps.Geocoder() : null;
      if (!geocoder) {
        applySelection(selectedAddress);
        return;
      }

      geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
        if (disposed || status !== "OK" || !results || results.length === 0) {
          applySelection(selectedAddress);
          return;
        }

        const firstResult = results[0];
        if (!firstResult) {
          applySelection(selectedAddress);
          return;
        }

        const fallbackResult: GooglePlaceResult = {
          addressComponents: firstResult.address_components?.map((component) => ({
            longText: component.long_name,
            shortText: component.short_name,
            types: component.types,
          })),
          formattedAddress: firstResult.formatted_address,
          location: parsedPlace.location,
        };

        const fallbackAddress = parseAddressFields(fallbackResult);
        applySelection({
          ...selectedAddress,
          city: selectedAddress.city || fallbackAddress.city,
          state: selectedAddress.state || fallbackAddress.state,
          postalCode: selectedAddress.postalCode || fallbackAddress.postalCode,
          country: selectedAddress.country || fallbackAddress.country,
        });
      });
    });

    return () => {
      disposed = true;
      listener.remove();
      pacObserver?.disconnect();
      autocompleteRef.current = null;
    };
  }, [apiKey, className, disabled, id, isLoaded, placeholder]);

  return (
    <Input
      ref={inputRef}
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      autoComplete="off"
    />
  );
}

export function GoogleAddressAutocompleteInput({
  id,
  value,
  placeholder,
  className,
  disabled,
  onChange,
  onAddressSelected,
}: GoogleAddressAutocompleteInputProps) {
  const [apiKey, setApiKey] = useState<string | null>(cachedGoogleMapsApiKey);
  const mountedRef = useRef(true);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  const fetchAndSetApiKey = useCallback(async (): Promise<string | null> => {
    const key = await getGoogleMapsApiKey();

    if (!mountedRef.current) {
      return null;
    }

    if (key) {
      setApiKey(key);
    }

    return key;
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const attemptLoad = async () => {
      const key = await fetchAndSetApiKey();
      if (key || !mountedRef.current || retryTimerRef.current) {
        return;
      }

      if (retryCountRef.current >= 3) {
        return;
      }

      // Retry because auth/session or app secret can become available after mount.
      retryCountRef.current += 1;
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        void attemptLoad();
      }, 3000);
    };

    void attemptLoad();

    return () => {
      mountedRef.current = false;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [fetchAndSetApiKey]);

  if (!apiKey) {
    return (
      <Input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => {
          void fetchAndSetApiKey();
        }}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
      />
    );
  }

  return (
    <GoogleAddressAutocompleteLoaded
      id={id}
      value={value}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      onChange={onChange}
      onAddressSelected={onAddressSelected}
      apiKey={apiKey}
    />
  );
}
